import React, { createContext, useState, useContext, useEffect, useCallback, ReactNode } from 'react';
import api from '../api';
import { useNavigate } from 'react-router-dom';
import { getPermissionsForRole } from '../utils/rolePermissions';

interface School {
  _id: string;
  name: string;
  email: string;
  address?: string;
  phone?: string;
  description?: string;
  city?: string;
  country?: string;
  subscriptionActive: boolean;
  subscriptionExpiry?: Date;
  staff: string[];
  students: string[];
  activeTermName?: string;
  termDurationMonths?: number;
  activeTermStartDate?: string;
  activeTermEndDate?: string;
  examTicketSubjects?: Array<{ subjectCode: string; subjectName: string; date?: string }>;
  examTicketSignatureUrl?: string;
  examTicketInstructions?: string;
  examTicketByClass?: Array<{
    className: string;
    examTicketSubjects?: Array<{ subjectCode: string; subjectName: string; date?: string }>;
    examTicketSignatureUrl?: string;
    examTicketInstructions?: string;
  }>;
  marksSubmissionDeadline?: string;
  logoUrl?: string;
  logoUpload?: {
    data?: string;
    contentType?: string;
    fileName?: string;
    originalName?: string;
  };
}

interface Staff {
  _id: string;
  name: string;
  email: string;
  role: string;
  className?: string;
  permissions?: Record<string, boolean>;
  school: string;
}

interface Student {
  _id: string;
  name: string;
  email: string;
  className?: string;
  registrationId?: string;
  department?: string;
  reportCardPublished?: boolean;
  examTicketPublished?: boolean;
  educationLevel: string;
  isSubscribed: boolean;
}

interface Payment {
  _id: string;
  amount: number;
  status: string;
  transactionId: string;
  createdAt: Date;
}

interface SchoolContextType {
  school: School | null;
  staff: Staff[];
  students: Student[];
  payments: Payment[];
  loading: boolean;
  initializing: boolean;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  loadSchoolData: () => Promise<void>;
  addStaff: (staffData: { name: string; email: string; password: string; role: string; className?: string }) => Promise<void>;
  updateStaffRole: (staffId: string, role: string, className?: string) => Promise<void>;
  deleteStaff: (staffId: string) => Promise<void>;
  makePayment: (amount: number, reference?: string, paymentProvider?: string) => Promise<void>;
  updateStudentClass: (studentId: string, className?: string, registrationId?: string, department?: string) => Promise<void>;
  publishStudentDocuments: (className: string, publishType: 'reportCard' | 'examTicket', studentIds?: string[], allExcept?: boolean) => Promise<void>;
  updateSchool: (updates: Partial<School>) => Promise<void>;
  updateSchoolProfile: (updates: { name?: string; email?: string; address?: string; phone?: string; description?: string; activeTermName?: string; activeTermStartDate?: string; activeTermEndDate?: string; termDurationMonths?: number; marksSubmissionDeadline?: string; logoUrl?: string; logoUpload?: { data?: string; mimeType?: string; filename?: string; originalName?: string }; examTicketClassName?: string; examTicketSubjects?: Array<{ subjectCode: string; subjectName: string; date?: string }>; examTicketSignatureUrl?: string; examTicketInstructions?: string }) => Promise<void>;
}

const SchoolContext = createContext<SchoolContextType | undefined>(undefined);

export const SchoolProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [school, setSchool] = useState<School | null>(null);
  const [staff, setStaff] = useState<Staff[]>([]);
  const [students, setStudents] = useState<Student[]>([]);
  const [payments, setPayments] = useState<Payment[]>([]);
  const [loading, setLoading] = useState(false);
  const [initializing, setInitializing] = useState(true);
  const navigate = useNavigate();

  const login = async (email: string, password: string) => {
    try {
      const response = await api.post('/schools/login', { email: email.trim().toLowerCase(), password });
      const { token, school: schoolData } = response.data;
      localStorage.setItem('school_token', token);
      localStorage.setItem('school', JSON.stringify(schoolData));
      setSchool(schoolData);
      navigate('/school/dashboard');
    } catch (error) {
      throw error;
    }
  };

  const logout = React.useCallback(() => {
    localStorage.removeItem('school_token');
    localStorage.removeItem('school');
    setSchool(null);
    setStaff([]);
    setStudents([]);
    setPayments([]);
    navigate('/school/login');
  }, [navigate]);

  const loadSchoolData = useCallback(async () => {
    if (!school) return;
    setLoading(true);
    try {
      const [staffRes, studentsRes, paymentsRes] = await Promise.all([
        api.get(`/staff/${school._id}/staff`, {
          headers: { 'x-auth-token': localStorage.getItem('school_token') }
        }),
        api.get(`/schools/${school._id}/students`, {
          headers: { 'x-auth-token': localStorage.getItem('school_token') }
        }),
        api.get('/payments', {
          headers: { 'x-auth-token': localStorage.getItem('school_token') }
        })
      ]);
      setStaff(staffRes.data.map((member: any) => ({
        ...member,
        permissions: member.permissions || getPermissionsForRole(member.role),
      })));
      setStudents(studentsRes.data);
      setPayments(paymentsRes.data);
    } catch (error) {
      console.error('Error loading school data:', error);
    } finally {
      setLoading(false);
    }
  }, [school]);

  const addStaff = async (staffData: { name: string; email: string; password: string; role: string; className?: string }) => {
    if (!school) return;
    const response = await api.post(`/staff/${school._id}/staff`, staffData, {
      headers: { 'x-auth-token': localStorage.getItem('school_token') }
    });
    const newStaff = {
      ...response.data.staff,
      permissions: response.data.staff.permissions || getPermissionsForRole(response.data.staff.role),
    };
    setStaff(prev => [...prev, newStaff]);
    await loadSchoolData();
  };

  const updateStaffRole = async (staffId: string, role: string, className?: string) => {
    const response = await api.put(`/staff/${staffId}/role`, { role, className }, {
      headers: { 'x-auth-token': localStorage.getItem('school_token') }
    });
    const updatedStaff = {
      ...response.data.staff,
      permissions: response.data.staff.permissions || getPermissionsForRole(response.data.staff.role),
    };
    setStaff(prev => prev.map(s => s._id === staffId ? updatedStaff : s));
    await loadSchoolData();
  };

  const deleteStaff = async (staffId: string) => {
    await api.delete(`/staff/${staffId}`, {
      headers: { 'x-auth-token': localStorage.getItem('school_token') }
    });
    setStaff(prev => prev.filter(s => s._id !== staffId));
    await loadSchoolData();
  };

  const makePayment = async (amount: number, reference?: string, paymentProvider?: string) => {
    const payload: Record<string, any> = { amount };
    if (reference) payload.reference = reference;
    if (paymentProvider) payload.paymentProvider = paymentProvider;

    const schoolToken = localStorage.getItem('school_token') || '';
    const response = await api.post('/payments', payload, {
      headers: {
        'x-auth-token': schoolToken,
        Authorization: schoolToken ? `Bearer ${schoolToken}` : undefined
      }
    });
    const nextPayment = response.data?.payment || response.data;
    if (nextPayment) {
      setPayments(prev => [...prev, nextPayment]);
    }
    // Reload school data to update subscription status
    await loadSchoolData();
  };

  const updateStudentClass = async (studentId: string, className?: string, registrationId?: string, department?: string) => {
    if (!school) return;
    const payload: Record<string, any> = {};
    if (typeof className === 'string') payload.className = className;
    if (typeof registrationId === 'string') payload.registrationId = registrationId;
    if (typeof department === 'string') payload.department = department;

    const response = await api.patch(
      `/schools/${school._id}/students/${studentId}`,
      payload,
      {
        headers: { 'x-auth-token': localStorage.getItem('school_token') }
      }
    );

    setStudents(prev => prev.map((student) =>
      student._id === studentId ? { ...student, ...response.data.student } : student
    ));
    await loadSchoolData();
  };

  const publishStudentDocuments = async (className: string, publishType: 'reportCard' | 'examTicket', studentIds: string[] = [], allExcept = false) => {
    if (!school) return;
    await api.patch(
      `/schools/${school._id}/students/publish`,
      { className, publishType, studentIds, allExcept },
      {
        headers: { 'x-auth-token': localStorage.getItem('school_token') }
      }
    );
    await loadSchoolData();
  };

  const updateSchool = async (updates: Partial<School>) => {
    if (!school) return;
    const response = await api.put(`/schools/${school._id}`, updates, {
      headers: { 'x-auth-token': localStorage.getItem('school_token') }
    });
    const updatedSchool = response.data.school || response.data;
    setSchool(updatedSchool);
    localStorage.setItem('school', JSON.stringify(updatedSchool));
  };

  const updateSchoolProfile = async (updates: { name?: string; email?: string; address?: string; phone?: string; description?: string; activeTermName?: string; activeTermStartDate?: string; activeTermEndDate?: string; termDurationMonths?: number; marksSubmissionDeadline?: string; logoUrl?: string; logoUpload?: { data?: string; mimeType?: string; filename?: string; originalName?: string }; examTicketClassName?: string; examTicketSubjects?: Array<{ subjectCode: string; subjectName: string; date?: string }>; examTicketSignatureUrl?: string; examTicketInstructions?: string }) => {
    if (!school) return;
    const response = await api.put(`/schools/${school._id}/profile`, updates, {
      headers: { 'x-auth-token': localStorage.getItem('school_token') }
    });
    const updatedSchool = response.data.school || response.data;
    setSchool(updatedSchool);
    localStorage.setItem('school', JSON.stringify(updatedSchool));
  };

  useEffect(() => {
    const token = localStorage.getItem('school_token');
    const storedSchool = localStorage.getItem('school');

    const restoreSchool = async () => {
      if (!token) {
        setInitializing(false);
        return;
      }

      if (storedSchool) {
        try {
          const parsedSchool = JSON.parse(storedSchool) as School;
          setSchool(parsedSchool);
          setInitializing(false);
          return;
        } catch (error) {
          console.warn('Failed to parse stored school data:', error);
          localStorage.removeItem('school');
        }
      }

      try {
        const response = await api.get('/schools/me', {
          headers: { 'x-auth-token': token }
        });
        localStorage.setItem('school', JSON.stringify(response.data));
        setSchool(response.data);
      } catch (error) {
        console.error('Unable to restore school session:', error);
        logout();
      } finally {
        setInitializing(false);
      }
    };

    restoreSchool();
  }, [logout]);

  useEffect(() => {
    if (school) {
      loadSchoolData();
    }
  }, [school, loadSchoolData]);

  return (
    <SchoolContext.Provider value={{
      school,
      staff,
      students,
      payments,
      loading,
      initializing,
      isAuthenticated: Boolean(school),
      login,
      logout,
      loadSchoolData,
      addStaff,
      updateStaffRole,
      deleteStaff,
      makePayment,
      updateStudentClass,
      publishStudentDocuments,
      updateSchool,
      updateSchoolProfile
    }}>
      {children}
    </SchoolContext.Provider>
  );
};

export const useSchool = () => {
  const context = useContext(SchoolContext);
  if (!context) {
    throw new Error('useSchool must be used within SchoolProvider');
  }
  return context;
};